import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { CodebaseTestResults } from '../types';

interface CodebaseTestResultsCardProps {
  results: CodebaseTestResults;
}

const CodebaseTestResultsCard: React.FC<CodebaseTestResultsCardProps> = ({ results }) => {
  const [expanded, setExpanded] = useState(false);
  const passRate = results.total > 0 ? (results.passed / results.total) * 100 : 0;
  const allPassed = results.failed === 0 && results.errors === 0;

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'passed':
        return <Chip label="Passed" size="small" color="success" icon={<CheckCircleIcon />} />;
      case 'failed':
        return <Chip label="Failed" size="small" color="error" icon={<ErrorIcon />} />;
      case 'error':
        return <Chip label="Error" size="small" color="warning" icon={<WarningIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mt: 1,
        mb: 1,
        borderColor: allPassed ? 'success.main' : 'error.main',
        borderWidth: 1,
      }}
    >
      <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {allPassed ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <ErrorIcon color="error" fontSize="small" />
          )}
          <Typography variant="subtitle2" fontWeight="bold">
            Generated Test Results: {results.passed}/{results.total} passed
          </Typography>
          {results.errors > 0 && (
            <Chip label={`${results.errors} errors`} size="small" color="warning" />
          )}
        </Stack>

        <LinearProgress
          variant="determinate"
          value={passRate}
          color={allPassed ? 'success' : 'error'}
          sx={{ mb: 1, height: 6, borderRadius: 3 }}
        />

        <Typography variant="caption" color="text.secondary">
          {results.passed} passed, {results.failed} failed, {results.errors} errors
        </Typography>

        {results.results.length > 0 && (
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            sx={{
              mt: 1,
              '&:before': { display: 'none' },
              bgcolor: 'transparent',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32 }}>
              <Typography variant="caption" color="text.secondary">
                {expanded ? 'Hide' : 'Show'} individual results ({results.results.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Stack spacing={0.5}>
                {results.results.map((r, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      {getStatusChip(r.status)}
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                        {r.name}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                      {r.duration > 0 ? `${(r.duration / 1000).toFixed(1)}s` : ''}
                    </Typography>
                  </Box>
                ))}
                {results.results
                  .filter((r) => r.error)
                  .map((r, i) => (
                    <Box
                      key={`error-${i}`}
                      sx={{
                        px: 1,
                        py: 0.5,
                        bgcolor: 'error.main',
                        color: 'error.contrastText',
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {r.name}: {r.error}
                    </Box>
                  ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};

export default CodebaseTestResultsCard;
